/**
 * Session 31 · Zentraler Tracking-Dispatcher.
 *
 * Zuständig für:
 *  - Bootstrap (Consent-Mode-Defaults, Public-Config-Fetch)
 *  - Consent-abhängiges Nachladen von Meta-Pixel + GA4 + Google Ads
 *  - Zentrales `trackEvent(name, params)` mit Consent-Kategorie-Gate
 *  - SPA-PageView-Dedup (kein Doppelfeuern pro Location)
 *  - Consent-Widerruf blockiert alle künftigen Events sofort
 *
 * Regeln:
 *  - Idempotent (Mehrfach-Mount produziert keine Duplikate).
 *  - Fehlertolerant (try/catch um alles Externe).
 *  - Keine PII an Meta/Google im Browser.
 */
import { readConsent } from "../consent";
import { installConsentModeDefaults, updateConsentMode } from "./consentMode";
import { initMetaPixel, fbqTrack, disableMetaPixel } from "./fbq";
import { initGA4, initGoogleAds, gtagEvent, disableGoogleTracking } from "./gtag";
import { syncMetricoolConsent, trackMetricoolRoute } from "./metricool";
import { syncPosthogConsent, trackPosthogRoute } from "./posthog";
import { EVENTS } from "./events";

// 03.11.2026: Testmodus kommt vom Server (TRACKING_TEST_MODE, /api/tracking/public-config); hier nur noch, wenn beim Bauen ausdrücklich gesetzt
const TEST_MODE = process.env.REACT_APP_TRACKING_TEST_MODE === "true";

// P0-E · Test-Kontext-Erkennung im Browser: Preview/Localhost-Hosts, Query-Flag
// oder Headless-User-Agent führen dazu, dass KEIN Event ausgeleitet wird.
// Ergänzt (nicht ersetzt) den TEST_MODE-Guard.
function _isTestContext() {
  try {
    if (typeof window === "undefined") return false;
    const host = (window.location.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0") return true;
    if (host.endsWith(".preview.emergentagent.com") || host.endsWith(".emergentagent.com")) return true;
    const q = new URLSearchParams(window.location.search);
    if ((q.get("pb24_test") || "").trim() === "1") return true;
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/(playwright|headlesschrome|puppeteer|selenium|phantomjs|python-requests)/.test(ua)) return true;
  } catch (_) { /* noop */ }
  return false;
}

let _bootstrapped = false;
let _publicConfig = null;
let _lastPageViewPath = null;
// Session 31 · Phase 4 fix: Consent-State initial aus localStorage lesen,
// damit `trackEvent()`-Aufrufe VOR dem asynchronen `initTracking()`-Abschluss
// nicht fälschlich als „no consent" abgewiesen werden (Race auf Success-Page).
let _consentState = (() => {
  try {
    const c = (typeof window !== "undefined") ? readConsent() : null;
    return { analytics: !!(c && c.analytics), marketing: !!(c && c.marketing) };
  } catch (_) { return { analytics: false, marketing: false }; }
})();

async function fetchPublicConfig() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const r = await fetch(`${base}/api/tracking/public-config`, { credentials: "omit" });
    if (!r.ok) return null;
    return await r.json();
  } catch (_) { return null; }
}

function applyLoaders() {
  if (!_publicConfig) return;
  const forceTest = TEST_MODE || _publicConfig.test_mode;

  // Analytics-Kategorie → GA4
  if (_consentState.analytics && _publicConfig.ga_measurement_id) {
    initGA4({ measurementId: _publicConfig.ga_measurement_id, testMode: forceTest });
  }
  // Marketing-Kategorie → Meta Pixel + Google Ads
  if (_consentState.marketing) {
    if (_publicConfig.meta_pixel_id) {
      initMetaPixel({ pixelId: _publicConfig.meta_pixel_id, testMode: forceTest });
    }
    if (_publicConfig.google_ads_conversion_id) {
      initGoogleAds({ conversionId: _publicConfig.google_ads_conversion_id, testMode: forceTest });
    }
  }
}

function syncConsentAndLoaders() {
  const c = readConsent() || { analytics: false, marketing: false };
  _consentState = { analytics: !!c.analytics, marketing: !!c.marketing };
  updateConsentMode(_consentState);

  if (!_consentState.marketing) disableMetaPixel();
  if (!_consentState.analytics && !_consentState.marketing) disableGoogleTracking();
  syncMetricoolConsent(_consentState.analytics);
  syncPosthogConsent(_consentState.analytics);

  applyLoaders();

  // SPA-PageView-Dedup zurücksetzen, damit erstes Event nach Consent-Grant feuert
  _lastPageViewPath = null;
  firePageView();
}

/** Öffentlicher Boot-Aufruf: wird von App.js einmalig beim Mount aufgerufen. */
export async function initTracking() {
  if (_bootstrapped) return;
  _bootstrapped = true;

  installConsentModeDefaults();
  _publicConfig = await fetchPublicConfig();
  if (!_publicConfig) return; // Backend nicht erreichbar → still fail

  syncConsentAndLoaders();

  window.addEventListener("pb24-consent-changed", syncConsentAndLoaders);

  // 03.11.2026: Klick auf Telefon/WhatsApp = Meta „Contact“ (nur mit Marketing-Zustimmung, fbqTrack prüft das)
  document.addEventListener("click", (e) => {
    try {
      const a = e.target?.closest?.("a[href]");
      if (!a || !_consentState.marketing || _isTestContext()) return;
      const h = a.getAttribute("href") || "";
      if (h.startsWith("tel:")) fbqTrack("Contact", { content_name: "Telefon" });
      else if (/wa\.me|whatsapp/i.test(h)) fbqTrack("Contact", { content_name: "WhatsApp" });
    } catch (_) { /* noop */ }
  }, true);

  // SPA-PageView bei jeder History-Änderung
  const onNav = () => firePageView();
  window.addEventListener("popstate", onNav);
  const wrap = (fn) => function () {
    const r = fn.apply(this, arguments);
    setTimeout(onNav, 0);
    return r;
  };
  try {
    window.history.pushState = wrap(window.history.pushState);
    window.history.replaceState = wrap(window.history.replaceState);
  } catch (_) { /* noop */ }
}

function firePageView() {
  const path = window.location.pathname + window.location.search;
  if (path === _lastPageViewPath) return;
  _lastPageViewPath = path;

  // GA4: nur bei Analytics-Consent
  if (_consentState.analytics) {
    gtagEvent("page_view", { page_location: window.location.href, page_path: path });
  }
  trackMetricoolRoute();
  trackPosthogRoute();
  // Meta: nur bei Marketing-Consent (nutzt fbq('track', 'PageView'), das der Initializer bereits einmal gefeuert hat;
  // für weitere SPA-Navigationen erneut auslösen)
  if (_consentState.marketing) {
    fbqTrack("PageView");
  }
}

/**
 * Zentrales Tracking-API.
 * @param {string} name  einer der Werte aus EVENTS
 * @param {object} params  optionale Payload-Daten
 * @param {"analytics"|"marketing"} minCategory  benötigter Consent-Level
 */
export function trackEvent(name, params = {}, minCategory = "analytics") {
  try {
    const _consentBlocked =
      (minCategory === "marketing" && !_consentState.marketing) ||
      (minCategory === "analytics" && !_consentState.analytics && !_consentState.marketing);
    // P0-E · Test-Kontext-Guard: kein Provider-Call in Preview/Localhost/Headless
    const _testCtx = _isTestContext();
    // Session 31 · Phase 4 · Reiner Debug-Trace für automatisierte Tests.
    // Aktiv NUR wenn die Testumgebung `window.__pb24TrackDebug = true` setzt.
    // Kein Netzwerk-Effekt. Feld `blockedByConsent` zeigt, ob der Dispatcher
    // das Event danach an gtag/fbq weiterreicht.
    if (typeof window !== "undefined" && window.__pb24TrackDebug) {
      (window.__pb24TrackCalls = window.__pb24TrackCalls || []).push({
        name, params, minCategory,
        blockedByConsent: _consentBlocked,
        blockedByTest: _testCtx,
        ts: Date.now(),
        consent: { analytics: _consentState.analytics, marketing: _consentState.marketing },
      });
    }
    if (_consentBlocked) return;
    if (_testCtx) return;  // niemals Live-Traffic aus Test-Kontexten

    // GA4-Namen sind identisch zu unseren Konstanten.
    if (_consentState.analytics) gtagEvent(name, params);

    // Meta-Mapping
    const metaMap = {
      [EVENTS.VIEW_CONTENT]: "ViewContent",
      [EVENTS.ADD_TO_CART]: "AddToCart",
      [EVENTS.BEGIN_CHECKOUT]: "InitiateCheckout",
      [EVENTS.ADD_PAYMENT_INFO]: "AddPaymentInfo",
      [EVENTS.PURCHASE]: "Purchase",
      [EVENTS.LEAD]: "Lead",
    };
    if (_consentState.marketing && metaMap[name]) {
      const metaParams = {};
      if (params.value != null) metaParams.value = params.value;
      if (params.currency) metaParams.currency = params.currency;
      if (params.event_id) metaParams.eventID = params.event_id;
      fbqTrack(metaMap[name], metaParams);
    }
  } catch (_) { /* Tracking-Fehler dürfen App niemals stören */ }
}

export { EVENTS };
